// src/api/collabApi.js
// All collaboration API calls. Thin wrappers around fetchWithAuth
// so components and the store never touch fetch directly.

import { fetchWithAuth, computeEffectiveOrgId } from "./fetchWithAuth";
import { useAuthStore } from "../stores/useAuthStore";

// ─── Events ──────────────────────────────────────────────────────────────────

/**
 * Load events for a context_key, newest first.
 * @param {string} contextKey
 * @param {{ limit?: number, before?: string }} opts
 * @returns {Promise<{ events: CollabEvent[] }>}
 */
export async function fetchEvents(contextKey, { limit = 50, before } = {}) {
  const params = new URLSearchParams({ context_key: contextKey, limit });
  if (before) params.set("before", before);
  const res = await fetchWithAuth(`/api/v1/collab/events?${params}`);
  if (!res.ok) throw new Error(`fetchEvents failed: ${res.status}`);
  return res.json();
}

/**
 * Post a new comment.
 * @param {string} contextKey
 * @param {string} body
 * @param {{ metadata?: object }} opts
 */
export async function postComment(contextKey, body, { metadata = {}, parent_id = null, event_type = "comment" } = {}) {
  const res = await fetchWithAuth("/api/v1/collab/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      context_key: contextKey,
      event_type,
      body,
      metadata,
      ...(parent_id ? { parent_id } : {}),
    }),
  });
  if (!res.ok) throw new Error(`postComment failed: ${res.status}`);
  return res.json();
}

/**
 * Post a linked reference (run, AOI, asset).
 * @param {string} contextKey
 * @param {{ ref_type: string, ref_id: string, ref_label: string }} ref
 */
export async function postLink(contextKey, ref) {
  const res = await fetchWithAuth("/api/v1/collab/events", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      context_key: contextKey,
      event_type: "link",
      body: "",
      metadata: ref,
    }),
  });
  if (!res.ok) throw new Error(`postLink failed: ${res.status}`);
  return res.json();
}

// ─── Notifications ────────────────────────────────────────────────────────────

/**
 * Fetch unread notifications for the current user.
 * @param {{ limit?: number }} opts
 * @returns {Promise<{ notifications: CollabNotification[], unread_count: number }>}
 */
export async function fetchNotifications({ limit = 20 } = {}) {
  const res = await fetchWithAuth(`/api/v1/collab/notifications?limit=${limit}`);
  if (!res.ok) throw new Error(`fetchNotifications failed: ${res.status}`);
  return res.json();
}

/**
 * Mark notifications as read.
 * @param {string[]|null} eventIds — pass null to mark ALL unread as read
 */
export async function markNotificationsRead(eventIds = null) {
  const res = await fetchWithAuth("/api/v1/collab/notifications/read", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(eventIds ? { event_ids: eventIds } : {}),
  });
  if (!res.ok) throw new Error(`markNotificationsRead failed: ${res.status}`);
  return res.json();
}

// ─── SSE stream ──────────────────────────────────────────────────────────────

/**
 * REM-422. Is an HTTP status a REFUSAL (stop retrying) or an OUTAGE (retry)?
 *
 * The stream route refuses with 400 (no context_key), 401 (token), 403 (org or
 * context not permitted) and 404. Those will not change by waiting, and REM-66
 * made stopping on them load-bearing. Everything else means the server or the
 * path to it is not answering properly right now: 5xx (database down, admin-api
 * restarting behind nginx's 502), 408 and 429. Those are worth retrying.
 */
export function isStreamRefusal(status) {
  return status === 400 || status === 401 || status === 403 || status === 404;
}

/**
 * REM-422. Learn the status EventSource hides.
 *
 * When EventSource reaches CLOSED, the server answered with a non-2xx, but
 * EventSource does not say which. So ask once, with fetch, and read only the
 * status line: the request is aborted as soon as the headers arrive, so a 200
 * (the stream is back) opens nothing the server keeps. The server's own
 * req.on("close") cleanup runs on the abort. A fetch that fails outright
 * (network down, connection refused) returns null: an outage, not a refusal.
 */
export async function probeStreamStatus(url) {
  const ctl = new AbortController();
  try {
    const res = await fetch(url, { signal: ctl.signal, cache: "no-store" });
    return res.status;
  } catch {
    return null;
  } finally {
    ctl.abort();
  }
}

/**
 * Open an SSE connection for a context_key.
 * Returns a cleanup function — call it to close the connection.
 *
 * @param {string}   contextKey
 * @param {function} onEvent   — called with a parsed event object on new_event
 * @param {function} onError   — called on connection error (optional), with
 *                               (err, { hardRefusal, status })
 * @param {function} onOpen    — called when the stream is open (optional)
 * @returns {function} unsubscribe
 */
export function subscribeToStream(contextKey, onEvent, onError, onOpen) {
  // EventSource can't send custom headers, so SSE-friendly credentials
  // are passed as query params and promoted server-side by
  // injectStreamCredentials before requireUser / resolveOrgContext run.
  //   ?token=<jwt>          → Authorization
  //   ?active_org=<uuid>    → X-Active-Org  (without this, platform_admin
  //                          users 403 because there's no user_orgs fallback
  //                          for admins in resolveOrgContext)
  const token = useAuthStore.getState().token ?? "";
  const activeOrg = computeEffectiveOrgId();

  const params = new URLSearchParams({ context_key: contextKey });
  if (token)     params.set("token",      token);
  if (activeOrg) params.set("active_org", activeOrg);
  const url = `/api/v1/collab/stream?${params}`;

  const es = new EventSource(url);

  es.addEventListener("new_event", (e) => {
    try {
      const event = JSON.parse(e.data);
      onEvent(event);
    } catch (err) {
      console.warn("[collab/stream] parse error", err);
    }
  });

  es.onopen = () => { onOpen?.(); };

  es.onerror = (err) => {
    // readyState is read HERE, before anything closes the stream, because it is
    // the only signal EventSource gives about WHY the error fired and closing
    // the stream destroys it. CONNECTING (0) means the browser lost a live
    // connection and intends to retry. CLOSED (2) means the server answered
    // with a non-2xx or the wrong content type, and the browser has given up.
    //
    // The distinction became load-bearing when the server started closing this
    // stream at token expiry (REM-66): the caller's retry now has a way to
    // reach a permanently-refusing endpoint, and needs to be able to tell.
    //
    // REM-422: CLOSED WAS READ AS "REFUSED", AND IT IS NOT ONLY THAT. A 500
    // (the database is down) and a 502 (nginx while admin-api restarts) also
    // leave CLOSED. The caller then stopped retrying for the life of the tab,
    // so after any outage longer than one 5 s retry, live updates were gone
    // until reload, silently. So CLOSED is now resolved by status, with one
    // probe: a refusal stops, anything else is an outage and is retried.
    if (es.readyState !== EventSource.CLOSED) {
      console.warn("[collab/stream] connection lost; will reconnect", err);
      onError?.(err, { hardRefusal: false, status: null });
      return;
    }
    probeStreamStatus(url).then((status) => {
      const hardRefusal = status !== null && isStreamRefusal(status);
      console.warn("[collab/stream] stream closed by the server", { status, hardRefusal });
      onError?.(err, { hardRefusal, status });
    });
  };

  // Return cleanup function
  return () => es.close();
}

// ─── Project members ─────────────────────────────────────────────────────────

/**
 * Fetch members of a project for assignee pickers.
 * @param {string} projectId
 * @returns {Promise<{ members: ProjectMember[] }>}
 */
export async function fetchProjectMembers(projectId) {
  const res = await fetchWithAuth(`/api/v1/collab/projects/${projectId}/members`);
  if (!res.ok) throw new Error(`fetchProjectMembers failed: ${res.status}`);
  return res.json();
}

/**
 * Update an event's metadata (e.g. flag status transition).
 * @param {string} eventId
 * @param {object} metadata — merged with existing metadata server-side
 */
export async function patchEventMetadata(eventId, metadata) {
  const res = await fetchWithAuth(`/api/v1/collab/events/${eventId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ metadata }),
  });
  if (!res.ok) throw new Error(`patchEventMetadata failed: ${res.status}`);
  return res.json();
}

// ─── Users (mention autocomplete) ────────────────────────────────────────────

/**
 * Search org members for @mention autocomplete.
 * @param {string} q — search string, min 1 char
 * @returns {Promise<{ users: CollabUser[] }>}
 */
export async function searchUsers(q = "") {
  const res = await fetchWithAuth(`/api/v1/collab/users?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(`searchUsers failed: ${res.status}`);
  return res.json();
}

